import { Undo } from "@ignite-analytics/icons";
import { Button } from "@mui/material";
import { useFormikContext } from "formik";
import React from "react";
import { FormattedMessage } from "react-intl";

import { Question, QuestionType } from "../../../globals/types";
import { initialValue } from "../../Form/helpers";
import { QuestionProps } from "../interfaces";

type Props = {
    question: Question;
};

const ID_PREFIX = "questionnaire-";

const blankValue = (question: Question) => {
    switch (question.type) {
        case QuestionType.MultiSelect:
            return [];
        default:
            return "";
    }
};

/**
 * Button for clearing an answer to a question in a questionnaire.
 * Takes the existing `formValue` of the field to determine the type of blank value to set,
 * and `setFormValue` to clear the value.
 */
const hasAnswer = (v: initialValue | undefined): boolean => {
    if (!v) return false;
    const hasValue = Array.isArray(v.value)
        ? v.value.some((x) => x && x.trim() !== "")
        : !!v.value && v.value.trim() !== "";
    return hasValue || (v.fileNames?.length ?? 0) > 0 || !!v.comment?.trim();
};

const ClearAnswer: React.FC<Props & Pick<QuestionProps, "saveDraft">> = ({ question, saveDraft }) => {
    const { setFieldValue, values } = useFormikContext<Record<string, initialValue>>();

    const answered = hasAnswer(values[question.id]);

    const onClick = () => {
        setFieldValue(question.id, {
            value: blankValue(question),
            fileNames: [],
            comment: "",
        });
        saveDraft([], [], "");
    };

    return (
        <Button
            onClick={onClick}
            size="xsmall"
            color="ghostGray"
            data-testid={`${ID_PREFIX}${question.id}-clearAnswerButton`}
            startIcon={<Undo />}
            aria-hidden={answered ? undefined : true}
            tabIndex={answered ? undefined : -1}
            sx={{
                textTransform: "none",
                fontWeight: 500,
                visibility: answered ? "visible" : "hidden",
                pointerEvents: answered ? undefined : "none",
            }}
        >
            <FormattedMessage defaultMessage="Reset" />
        </Button>
    );
};

export default ClearAnswer;
